/**
 * @fileoverview EditCategoryModal Component
 *
 * Modal for editing category name, starting balance, and transaction type attributes.
 * Displays warnings when attempting to edit starting balance with closed months.
 * Validates that at least one transaction type is selected.
 *
 * @module components/CategoryManagement/EditCategoryModal
 */
import { useState, useEffect } from 'react';
import ToggleSwitch from '../shared/ToggleSwitch';
import ButtonSpinner from '../Loading/ButtonSpinner';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import styles from './EditCategoryModal.module.css';

/**
 * EditCategoryModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Object} props.category - Category object being edited
 * @param {Function} props.onSave - Callback when save is clicked
 * @param {boolean} props.hasClosedMonths - Whether there are closed months before this category
 */
function EditCategoryModal({ isOpen, onClose, category, onSave, hasClosedMonths }) {
    const [categoryName, setCategoryName] = useState('');
    const [startingBalance, setStartingBalance] = useState('');
    const [allowExpense, setAllowExpense] = useState(false);
    const [allowTransfer, setAllowTransfer] = useState(false);
    const [error, setError] = useState('');
    const [validationError, setValidationError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    useModalScrollLock(isOpen);

    // Reset saving state when modal opens
    useEffect(() => {
        if (isOpen) {
            setIsSaving(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (category) {
            setCategoryName(category.categoryName);
            setStartingBalance(category.startingBalance || '');
            setAllowExpense(category.allowExpense || false);
            setAllowTransfer(category.allowTransfer || false);
        }
    }, [category]);

    if (!isOpen || !category) return null;

    const handleSave = async () => {
        if (!categoryName.trim()) {
            setError('Category name is required');
            return;
        }

        // Validate that at least one transaction type is selected
        if (!allowExpense && !allowTransfer) {
            setValidationError('Please select at least one transaction type');
            return;
        }

        // Clear validation error if valid
        setValidationError('');

        // Validate starting balance if provided
        if (startingBalance && isNaN(parseFloat(startingBalance))) {
            setError('Starting balance must be a valid number');
            return;
        }

        setIsSaving(true);
        try {
            await onSave({
                categoryKey: category.categoryKey,
                categoryName: categoryName.trim(),
                startingBalance: startingBalance ? parseFloat(startingBalance) : null,
                allowExpense,
                allowTransfer
            });
            // If onSave succeeds and doesn't close the modal, we should reset
            // But typically the parent will close the modal on success
        } catch (error) {
            // Re-enable button on error
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setError('');
        setValidationError('');
        onClose();
    };

    return (
        <>
            {/* Dark Overlay */}
            <div className={styles.overlay} onClick={handleClose}>
                {/* Modal Content */}
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    {/* Close X Button */}
                    <button
                        onClick={handleClose}
                        disabled={isSaving}
                        className={styles.closeButton}
                        aria-label="Close"
                    >
                        ×
                    </button>

                    {/* Modal Header */}
                    <div className={styles.header}>
                        <h2 className={styles.title}>Edit Category</h2>
                    </div>

                    {/* Modal Body */}
                    <div className={styles.body}>
                        {error && (
                            <div className={styles.error}>
                                {error}
                            </div>
                        )}

                        {hasClosedMonths && (
                            <div className={styles.warning}>
                                ⚠️ Warning: There are closed months in this budget. You will need to open them before updating the starting balance takes effect.
                            </div>
                        )}

                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Category Name:</label>
                            <input
                                type="text"
                                value={categoryName}
                                onChange={(e) => {
                                    setCategoryName(e.target.value);
                                    setError('');
                                }}
                                className={styles.input}
                                placeholder="Enter category name"
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Starting Balance:</label>
                            <input
                                type="number"
                                step="0.01"
                                value={startingBalance}
                                onChange={(e) => {
                                    setStartingBalance(e.target.value);
                                    setError('');
                                }}
                                className={styles.input}
                                placeholder="0.00"
                            />
                            <span className={styles.helpText}>
                                Leave blank for $0.00 starting balance
                            </span>
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Transaction Types:</label>
                            <div className={styles.toggleGroup}>
                                <ToggleSwitch
                                    checked={allowExpense}
                                    onChange={(checked) => {
                                        setAllowExpense(checked);
                                        setValidationError('');
                                    }}
                                    label="Expense"
                                />
                                <ToggleSwitch
                                    checked={allowTransfer}
                                    onChange={(checked) => {
                                        setAllowTransfer(checked);
                                        setValidationError('');
                                    }}
                                    label="Transfer"
                                />
                            </div>
                            {validationError && (
                                <span className={styles.errorText}>
                                    {validationError}
                                </span>
                            )}
                            <span className={styles.helpText}>
                                Select at least one transaction type for this category
                            </span>
                        </div>
                    </div>

                    {/* Modal Footer */}
                    <div className={styles.footer}>
                        <div className={styles.buttonGroup}>
                            <button
                                onClick={handleClose}
                                disabled={isSaving}
                                className={styles.cancelButton}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className={styles.saveButton}
                            >
                                {isSaving && <ButtonSpinner />}
                                {isSaving ? 'Saving Changes...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default EditCategoryModal;
